/**
 * RS Customizer — Bubble Letter Necklace
 * Visual preview renderer for bubble-letter-necklace products.
 * Fetches per-variant metaobject config (SVG path guide, backgrounds, letter images)
 * inline from the Liquid endpoint — no GraphQL required. Letters are keyed by character
 * directly from the metaobject's letters list-reference field.
 *
 * Rendering approach: SVG path-based — character images are placed at points along
 * the path using getPointAtLength(), rotated to follow the curve.
 *
 * MTO input validation is handled by rs-product-mto-input.js. ATC gating is managed by rs-product-core.js.
 * This file handles ONLY the visual preview.
 */
(function () {
  'use strict';

  const LIQUID_VIEW = 'metaobject-bubble-letter-necklace';

  // Scaling constants matching the paracord implementation.
  const BASELINE_WIDTH = 1110;
  const MIN_WIDTH = 300;
  const MAX_WIDTH = 1110;

  let _injectedSlideId = null;
  let _baseConfig = null;
  let _letters = null;
  let _variantConfig = null;
  let _backgroundsData = null;
  let _thumbnailsData = null;
  let _currentVariantTitle = null;
  let _resizeTimer = null;

  // ─── Per-variant data loader ────────────────────────────────────

  function _loadVariantData(variantTitle) {
    const productTitle = (_baseConfig && _baseConfig.product_title) || '';
    const variantKey = productTitle + ' - ' + variantTitle;
    const variantData = ((_baseConfig && _baseConfig.customizer_data) || {})[variantKey] || {};

    // Build _letters from the inline characters array.
    _letters = {};
    (variantData.characters || []).forEach(function (ch) {
      if (ch.character && ch.imagePath) {
        _letters[ch.character] = {
          name: ch.character,
          image: ch.imagePath,
          image_width: ch.imageWidth || variantData.charWidth || 60,
          image_height: ch.imageHeight || variantData.charHeight || 60,
          rotation: ch.rotation || 0,
          letter_spacing: ch.letterSpacing || 0,
          letter_spacing_override: ch.letterSpacingOverride || 0,
        };
      }
    });

    // Build _variantConfig from the variant-level fields.
    _variantConfig = {
      letter_height: variantData.charHeight || 60,
      letter_width: variantData.charWidth || 60,
      letter_uppercase: variantData.upperCase !== false,
      y_offset: variantData.yOffset || 0,
      global_spacing: variantData.globalSpacing || 6,
      svg_guide_content: variantData.svgGuide || null,
      svg_view_box_width: variantData.svgViewBoxWidth || 1000,
      svg_view_box_height: variantData.svgViewBoxHeight || 1200,
      letter_scale: 1.0,
    };

    _backgroundsData = { default: variantData.background || null };
    _thumbnailsData = { default: variantData.thumbnail || null };
  }

  // ─── DOM helpers ────────────────────────────────────────────────

  function _getMtoText() {
    const fields = document.querySelectorAll('[data-mto-field]');
    let text = '';
    fields.forEach(function (field) {
      text += field.value;
    });
    const trimmed = text.trim();
    return (_variantConfig && _variantConfig.letter_uppercase) ? trimmed.toUpperCase() : trimmed;
  }

  // ─── Data fetching ──────────────────────────────────────────────

  async function _fetchBaseConfig() {
    const response = await fetch(
      window.RSProduct.config.productUrl + '?view=' + LIQUID_VIEW
    );
    return response.json();
  }

  // ─── Background / thumbnail management ─────────────────────────

  function _getBackgroundUrl() {
    if (!_backgroundsData) return null;
    if (_backgroundsData['default']) return _backgroundsData['default'];
    const key = Object.keys(_backgroundsData).find(function (k) { return _backgroundsData[k]; });
    return key ? _backgroundsData[key] : null;
  }

  function _getBackgroundForVariant() {
    return _getBackgroundUrl();
  }

  function _getThumbnailUrl() {
    const thumbs = _thumbnailsData || {};
    if (thumbs['default']) return thumbs['default'];
    const key = Object.keys(thumbs).find(function (k) { return thumbs[k]; });
    return key ? thumbs[key] : null;
  }

  // Override the gallery thumbnail image with the dedicated thumbnail URL from the metaobject.
  // gallery.inject creates a thumbnail from the main background image; the metaobject provides
  // a separate smaller thumbnail image specifically designed for the gallery strip.
  function _applyThumbnailImage() {
    const thumbUrl = _getThumbnailUrl();
    if (!thumbUrl || !_injectedSlideId) return;

    const bgUrl = _getBackgroundUrl();
    const allThumbs = document.querySelectorAll('.product__thumb img, .splide__slide--thumbnail img');
    if (bgUrl) {
      allThumbs.forEach(function (img) {
        if (img.src && img.src.indexOf(bgUrl.split('?')[0]) !== -1) {
          img.src = thumbUrl;
        }
      });
    }
  }

  // ─── Letter key lookup ──────────────────────────────────────────

  function _getLetterKey(char) {
    if (_letters && _letters[char]) return char;

    if (_letters) {
      const keys = Object.keys(_letters);
      const found = keys.find(function (k) {
        return k.toLowerCase() === char.toLowerCase();
      });
      if (found) return found;
    }

    return char.toUpperCase();
  }

  // ─── Canvas management ──────────────────────────────────────────

  function _getCanvas() {
    if (!_injectedSlideId) return null;

    const slide = document.querySelector(
      '[data-rs-injected="' + _injectedSlideId + '"]'
    );
    if (!slide) return null;

    const customizerView = slide.querySelector('.customizer-view');
    if (!customizerView) return null;

    let canvas = customizerView.querySelector('.rs-customizer-canvas');
    if (!canvas) {
      canvas = _buildCanvas(customizerView);
    }
    return canvas;
  }

  function _buildCanvas(customizerView) {
    const canvas = document.createElement('div');
    canvas.className = 'rs-customizer-canvas';

    // Inject SVG path guide — kept in DOM (hidden) for getPointAtLength() measurements.
    if (_variantConfig && _variantConfig.svg_guide_content) {
      const temp = document.createElement('div');
      temp.innerHTML = _variantConfig.svg_guide_content;

      let svgEl = temp.querySelector('svg');
      if (!svgEl) {
        // svg_guide_content might be just a <path> element — wrap it in an SVG.
        const pathEl = temp.querySelector('path');
        if (pathEl) {
          svgEl = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
          const viewBoxWidth = _variantConfig.svg_view_box_width || BASELINE_WIDTH;
          const viewBoxHeight = _variantConfig.svg_view_box_height || 880;
          svgEl.setAttribute('viewBox', '0 0 ' + viewBoxWidth + ' ' + viewBoxHeight);
          svgEl.appendChild(pathEl.cloneNode(true));
        }
      }

      if (svgEl) {
        svgEl.classList.add('rs-customizer-path-svg');
        svgEl.setAttribute('aria-hidden', 'true');
        svgEl.setAttribute('focusable', 'false');
        const pathEl = svgEl.querySelector('path');
        if (pathEl) {
          pathEl.classList.add('rs-customizer-path');
        }
        canvas.appendChild(svgEl);
      }
    }

    const letterContainer = document.createElement('div');
    letterContainer.className = 'rs-customizer-letters';
    canvas.appendChild(letterContainer);

    customizerView.appendChild(canvas);
    return canvas;
  }

  function _getLetterContainer(canvas) {
    return canvas ? canvas.querySelector('.rs-customizer-letters') : null;
  }

  function _getPathElement(canvas) {
    if (!canvas) return null;
    const svgEl = canvas.querySelector('.rs-customizer-path-svg');
    if (!svgEl) return null;
    return svgEl.querySelector('path');
  }

  // ─── Letter rendering ───────────────────────────────────────────

  function _renderLetters() {
    const text = _getMtoText();
    const canvas = _getCanvas();
    if (!canvas) return;

    const letterContainer = _getLetterContainer(canvas);
    if (!letterContainer) return;

    letterContainer.querySelectorAll('[data-rs-customizer-item]').forEach(function (el) {
      el.remove();
    });

    if (!text || !_letters) return;

    const chars = text.split('').filter(function (ch) {
      const letterKey = _getLetterKey(ch);
      return _letters[letterKey] && _letters[letterKey].image;
    });

    if (!chars.length) return;

    const pathEl = _getPathElement(canvas);
    if (!pathEl) return;

    const positions = _calculatePositions(chars, pathEl, letterContainer);
    _mountLetters(chars, positions, letterContainer);
  }

  function _calculatePositions(chars, pathEl, letterContainer) {
    const pathLength = pathEl.getTotalLength();
    if (!Number.isFinite(pathLength) || pathLength <= 0) return [];

    const containerRect = letterContainer.getBoundingClientRect();
    const viewBoxWidth = (_variantConfig && _variantConfig.svg_view_box_width) || BASELINE_WIDTH;
    const viewBoxHeight = (_variantConfig && _variantConfig.svg_view_box_height) || 880;
    const scaleX = containerRect.width / viewBoxWidth;
    const scaleY = containerRect.height / viewBoxHeight; // eslint-disable-line no-unused-vars

    // Calculate total spacing needed to center the sequence along the path.
    let totalSpacing = 0;
    for (let i = 0; i < chars.length - 1; i++) {
      totalSpacing += _getCharSpacing(chars[i], chars[i + 1], containerRect.width, scaleX);
    }

    let startPos = (pathLength - totalSpacing) / 2;
    if (startPos < 0) startPos = 0;

    const positions = [];
    let currentPos = startPos;
    const yOffset = (_variantConfig && _variantConfig.y_offset) || 0;

    for (let i = 0; i < chars.length; i++) {
      const clamped = Math.min(Math.max(currentPos, 0), pathLength);
      const point = pathEl.getPointAtLength(clamped);
      const angle = _getAngleAt(clamped, pathEl, pathLength);
      const letterKey = _getLetterKey(chars[i]);
      const letterData = _letters[letterKey];
      const rotation = letterData ? (letterData.rotation || 0) : 0;

      const angleRad = (angle * Math.PI) / 180;
      const offsetX = -Math.sin(angleRad) * yOffset;
      const offsetY = Math.cos(angleRad) * yOffset;

      positions.push({
        x: (point.x + offsetX) * scaleX,
        y: (point.y + offsetY) * scaleX,
        rotation: angle + rotation,
        data: letterData,
        char: chars[i],
      });

      if (i < chars.length - 1) {
        const spacing = _getCharSpacing(chars[i], chars[i + 1], containerRect.width, scaleX);
        if (Number.isFinite(spacing) && spacing > 0) {
          currentPos += spacing;
        }
      }
    }

    return positions;
  }

  function _mountLetters(chars, positions, letterContainer) {
    positions.forEach(function (posData, i) {
      if (!posData.data || !posData.data.image) return;

      const wrapper = document.createElement('div');
      wrapper.setAttribute('data-rs-customizer-item', String(i));
      wrapper.className = 'rs-customizer-item';

      const img = document.createElement('img');
      img.src = posData.data.image;
      img.alt = posData.char;
      img.className = 'rs-customizer-letter-img';

      const height = _getResponsiveHeight();
      const width = _getLetterWidth(posData.char, height);

      img.style.width = width + 'px';
      img.style.height = height + 'px';

      img.style.opacity = '0';
      img.style.transition = 'opacity 0.3s';
      img.addEventListener('load', function () { img.style.opacity = '1'; });
      img.addEventListener('error', function () { img.style.opacity = '1'; });

      wrapper.style.left = posData.x + 'px';
      wrapper.style.top = posData.y + 'px';
      wrapper.style.transform = 'translate(-50%, -50%) rotate(' + posData.rotation + 'deg)';

      wrapper.appendChild(img);
      letterContainer.appendChild(wrapper);
    });
  }

  // ─── Sizing helpers ─────────────────────────────────────────────

  function _getScaleFactor() {
    if (!_injectedSlideId) return 1.0;

    const slide = document.querySelector(
      '[data-rs-injected="' + _injectedSlideId + '"]'
    );
    const containerWidth = slide ? slide.getBoundingClientRect().width : BASELINE_WIDTH;

    let scale;
    if (containerWidth < MIN_WIDTH) {
      scale = 0.6;
    } else if (containerWidth > MAX_WIDTH) {
      scale = 1.0;
    } else {
      const progress = (containerWidth - MIN_WIDTH) / (MAX_WIDTH - MIN_WIDTH);
      scale = 0.6 + progress * 0.4;
    }

    if (window.innerWidth <= 1024) scale *= 0.8;

    return scale;
  }

  function _getResponsiveHeight() {
    const baseHeight = (_variantConfig && _variantConfig.letter_height) || 60;
    const letterScale = (_variantConfig && _variantConfig.letter_scale) || 1.0;
    const scaledHeight = baseHeight * letterScale * _getScaleFactor();
    return Number.isFinite(scaledHeight) && scaledHeight > 0 ? scaledHeight : 60;
  }

  function _getLetterWidth(char, height) {
    const letterKey = _getLetterKey(char);
    const letterData = _letters && _letters[letterKey];

    if (letterData && letterData.image_width && letterData.image_height) {
      return height * (letterData.image_width / letterData.image_height);
    }

    const baseWidth = (_variantConfig && _variantConfig.letter_width) || 60;
    const letterScale = (_variantConfig && _variantConfig.letter_scale) || 1.0;
    const scaledWidth = baseWidth * letterScale * _getScaleFactor();
    return Number.isFinite(scaledWidth) && scaledWidth > 0 ? scaledWidth : 60;
  }

  function _getCharSpacing(currentChar, nextChar, containerWidth, scaleX) {
    const height = _getResponsiveHeight();
    const currentWidth = _getLetterWidth(currentChar, height);
    const nextWidth = _getLetterWidth(nextChar, height);

    const halfWidths = currentWidth / 2 + nextWidth / 2;

    const letterKey = _getLetterKey(currentChar);
    const letterData = _letters && _letters[letterKey];

    // letterSpacingOverride is a per-letter gap in display pixels.
    // letter_spacing (letter_spacing_decimal) was never used for spacing in the old theme
    // renderer — only letterSpacingOverride and globalSpacing were consumed.
    let spacingGap;
    if (letterData && typeof letterData.letter_spacing_override === 'number' && letterData.letter_spacing_override !== 0) {
      spacingGap = letterData.letter_spacing_override;
    } else {
      spacingGap = (_variantConfig && _variantConfig.global_spacing !== undefined)
        ? _variantConfig.global_spacing
        : 6;
    }

    const containerScale = Math.min((containerWidth || BASELINE_WIDTH) / BASELINE_WIDTH, 1.0);
    spacingGap *= containerScale;

    const finalSpacingPx = halfWidths + spacingGap;

    const result = scaleX > 0 ? finalSpacingPx / scaleX : finalSpacingPx;
    return Number.isFinite(result) && result > 0 ? result : 20;
  }

  function _getAngleAt(position, pathEl, pathLength) {
    const delta = Math.max(2, pathLength * 0.002);
    const p1 = pathEl.getPointAtLength(Math.max(0, position - delta));
    const p2 = pathEl.getPointAtLength(Math.min(pathLength, position + delta));
    const angle = Math.atan2(p2.y - p1.y, p2.x - p1.x) * (180 / Math.PI);
    return Number.isFinite(angle) ? angle : 0;
  }

  // ─── Event handlers ─────────────────────────────────────────────

  function _handleInput(event) {
    if (!event.target.matches('[data-mto-field]')) return;
    _renderLetters();
  }

  function _handleInputFocus(event) {
    if (!event.target.matches('[data-mto-field]')) return;
    if (_injectedSlideId) {
      window.RSProduct.gallery.navigate(_injectedSlideId);
    }
  }

  function _handleVariantChange(event) {
    if (!event.data || event.data.sectionId !== window.RSProduct.sectionId) return;

    const variant = event.data.variant;
    if (!variant || !variant.title) return;

    _currentVariantTitle = variant.title;

    // Re-build _letters, _variantConfig, _backgroundsData, _thumbnailsData for the new variant.
    _loadVariantData(_currentVariantTitle);

    // Gallery HTML is replaced by rs-product-core's variant handler — remove old slide first.
    if (_injectedSlideId) {
      window.RSProduct.gallery.remove(_injectedSlideId);
      _injectedSlideId = null;
    }

    const backgroundUrl = _getBackgroundForVariant();
    if (backgroundUrl) {
      _injectedSlideId = window.RSProduct.gallery.inject(backgroundUrl, 1);
      setTimeout(_applyThumbnailImage, 300);
    }

    const existingText = _getMtoText();
    if (existingText) {
      _renderLetters();
      if (_injectedSlideId) {
        window.RSProduct.gallery.navigate(_injectedSlideId);
      }
    }
  }

  function _onResize() {
    clearTimeout(_resizeTimer);
    _resizeTimer = setTimeout(function () {
      const text = _getMtoText();
      if (text) _renderLetters();
    }, 250);
  }

  // ─── Initialization ─────────────────────────────────────────────

  async function _init() {
    if (!window.RSProduct.config.productHandle) return;

    let baseConfig;
    try {
      baseConfig = await _fetchBaseConfig();
    } catch (err) {
      console.error('[Bubble Letter Necklace] Data fetch failed:', err);
      return;
    }

    if (!baseConfig || baseConfig.success === false) {
      console.error('[Bubble Letter Necklace] Metaobject config not found:', baseConfig && baseConfig.error);
      return;
    }

    _baseConfig = baseConfig;

    const initialVariantId = String(window.RSProduct.config.initialVariantId || '');
    const variantNames = baseConfig.variant_names || {};
    const fullName = variantNames[initialVariantId] || '';
    const parts = fullName.split(' - ');
    _currentVariantTitle = parts.length > 1 ? parts[parts.length - 1] : fullName;

    _loadVariantData(_currentVariantTitle);

    const backgroundUrl = _getBackgroundForVariant();
    if (backgroundUrl) {
      _injectedSlideId = window.RSProduct.gallery.inject(backgroundUrl, 1);
      setTimeout(_applyThumbnailImage, 300);
    }

    document.addEventListener('input', _handleInput);
    document.addEventListener('focusin', _handleInputFocus);
    window.addEventListener('resize', _onResize);

    if (window.subscribe && window.theme && window.theme.PUB_SUB_EVENTS) {
      window.subscribe(window.theme.PUB_SUB_EVENTS.variantChange, _handleVariantChange);
    }

    const existingText = _getMtoText();
    if (existingText) {
      _renderLetters();
    }
  }

  document.addEventListener('DOMContentLoaded', function () {
    if (!window.RSProduct) return;
    _init();
  });
})();
